import { useState } from 'react'
import {
  ICompany,
  IAttendanceEmployee,
  IAttendanceEmployeeAttendance,
  AttendanceEmployeeStatusEnum,
  IHttpError,
  useAttendanceDeleteEmployeeMutation
} from '@/@data'
import { useRouteQuery } from '@/hooks/useRouteQuery'
import { useTranslator } from '@/hooks/useTranslator'
import { Box, Checkbox, Divider, MenuItem, Menu, Tooltip, Zoom, IconButton } from '@mui/material'
import { TablePagination, TablePaginationProps } from '@/@core/components/mui/TablePagination'
import { UserAvatar } from '@/components/common/UserAvatar'
import InputDateRangerField from '@/components/common/input/InputDateRangerField'
import { Button } from '@/@core/components/mui/Button'
import { useNotification } from '@/hooks'
import dayjs from 'dayjs'

interface DetailAttendanceCompanyTableProps {
  data: IAttendanceEmployee[]
  dataNormal: any
  meta: unknown
  month?: string
  onRowClick?: (employer: ICompany) => void
  onEdit?: (employer: ICompany) => void
  refetch: () => void
  onCheckboxChange?: (
    newIsAllChecked: boolean,
    newExcludedUsers: Set<number>,
    newSelectedUsers: Set<number>,
    isAll: number,
    excludeUserIds: number[],
    userIds: number[]
  ) => void
  isAllChecked?: boolean
  excludedUsers?: Set<number>
  selectedUsers?: Set<number>
  methods?: any
  minDate?: string
  maxDate?: string
  attendanceAssumption?: any
  onSuccess?: () => void
  bulkChanges?: {
    [attendanceId: string]: {
      attendance_id: number
      attendance: Array<{
        date: number
        day_of_month?: string
        status: string
      }>
    }
  }
  setBulkChanges?: (changes: any) => void
}

const getAttendanceStatus = (attendance: IAttendanceEmployeeAttendance[], date: number) => {
  const dayAttendance = attendance.find(a => a.date === date)

  return dayAttendance?.status || AttendanceEmployeeStatusEnum.NonWorkingDay
}

const getAttendanceDotColor = (status: string) => {
  switch (status) {
    case AttendanceEmployeeStatusEnum.WorkingDay:
      return '#577dfd'
    case AttendanceEmployeeStatusEnum.Absent:
      return '#ff7074'
    case AttendanceEmployeeStatusEnum.NonWorkingDay:
      return '#c0bec5'
    default:
      return ''
  }
}

const countWorkingDays = (attendance: IAttendanceEmployeeAttendance[]) => {
  return attendance.filter(a => a.status === AttendanceEmployeeStatusEnum.WorkingDay).length
}

export const AttendanceReconcileTable = ({
  data,
  dataNormal,
  meta,
  methods,
  onCheckboxChange,
  selectedUsers = new Set(),
  minDate,
  maxDate,
  attendanceAssumption,
  onSuccess,
  bulkChanges = {},
  setBulkChanges
}: DetailAttendanceCompanyTableProps) => {
  const { searchQuery } = useRouteQuery()
  const { __ } = useTranslator()
  const n = useNotification()
  const { ConfirmDialog } = n
  const { id } = useParams<{ id: string }>()

  // Calculate selected count
  const getSelectedCount = () => {
    return selectedUsers.size
  }

  const attendanceEmployeeStatusLabel = {
    [AttendanceEmployeeStatusEnum.WorkingDay]: __('Working Day'),
    [AttendanceEmployeeStatusEnum.Absent]: __('Absent'),
    [AttendanceEmployeeStatusEnum.NonWorkingDay]: __('Non Working Day')
  }

  const monthParam = searchQuery.get('month') || dayjs().format('YYYY-MM')
  const selectedMonth = dayjs(monthParam)
  const currentMonth = dayjs()
  const isCurrentMonth = selectedMonth.isSame(currentMonth, 'month')
  const currentDay = isCurrentMonth ? currentMonth.date() : null

  const headerWorkingListRaw: Array<{ date: number; day_of_month?: string }> =
    (dataNormal?.attendance?.workingList as Array<{ date: number; day_of_month?: string }> | undefined) ||
    Array.from({ length: selectedMonth.daysInMonth() }, (_, i) => ({ date: i + 1 }))

  const availableDates = new Set<number>()

  for (const emp of data || []) {
    for (const a of emp.attendance || []) {
      if (typeof a?.date === 'number') availableDates.add(a.date)
    }
  }

  const headerWorkingList = [
    ...headerWorkingListRaw.filter(d => availableDates.has(d.date)),
    ...headerWorkingListRaw.filter(d => !availableDates.has(d.date))
  ]

  const dataWithId = data || []

  // Helper function để merge attendance từ data gốc và bulkChanges
  const getEmployeeAttendance = (employee: IAttendanceEmployee) => {
    const attendanceId = employee.id
    const originalAttendance = employee.attendance
    const changedAttendance = bulkChanges[attendanceId]?.attendance || []

    if (!bulkChanges[attendanceId]) {
      return originalAttendance
    }

    const attendanceMap = new Map()

    originalAttendance.forEach(a => {
      attendanceMap.set(a.date, a)
    })

    changedAttendance.forEach((a: any) => {
      attendanceMap.set(a.date, a)
    })

    return Array.from(attendanceMap.values())
  }

  const handleCheckAll = (checked: boolean) => {
    const newBulkChanges = { ...bulkChanges }

    if (checked) {
      const currentPageIds = dataWithId.map(emp => Number(emp.id))
      const newSelectedUsers = new Set([...selectedUsers, ...currentPageIds])
      const idsArray = Array.from(newSelectedUsers)

      dataWithId.forEach(employee => {
        const employeeId = Number(employee.id)

        if (!newBulkChanges[employeeId]) {
          newBulkChanges[employeeId] = {
            attendance_id: employeeId,
            attendance: []
          }
        }
      })

      onCheckboxChange?.(false, new Set(), newSelectedUsers, 0, [], idsArray)

      if (setBulkChanges) {
        setBulkChanges(newBulkChanges)
      }
    } else {
      const currentPageIds = new Set(dataWithId.map(emp => Number(emp.id)))
      const newSelectedUsers = new Set([...selectedUsers].filter(id => !currentPageIds.has(id)))
      const idsArray = Array.from(newSelectedUsers)

      currentPageIds.forEach(id => {
        delete newBulkChanges[id]
      })

      onCheckboxChange?.(false, new Set(), newSelectedUsers, 0, [], idsArray)

      if (setBulkChanges) {
        setBulkChanges(newBulkChanges)
      }
    }
  }

  const { updateQuery } = useRouteQuery()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectionAnchorEl, setSelectionAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const selectionOpen = Boolean(selectionAnchorEl)

  // Function để apply status vào date range
  const applyStatusToRange = (statusKey: string, startDate: string, endDate: string, showErrors = true) => {
    if (!startDate || !endDate) {
      if (showErrors) n.error(__('Please select date range'))

      return
    }

    if (!statusKey) {
      if (showErrors) n.error(__('Please select status'))

      return
    }

    if (selectedUsers.size === 0) {
      if (showErrors) n.error(__('Please select at least one employee'))

      return
    }

    const newBulkChanges = { ...bulkChanges }
    const start = dayjs(startDate)
    const end = dayjs(endDate)

    const selectedEmployees = dataWithId.filter(emp => selectedUsers.has(Number(emp.id)))

    selectedEmployees.forEach(employee => {
      const employeeId = Number(employee.id)

      if (!newBulkChanges[employeeId]) {
        newBulkChanges[employeeId] = {
          attendance_id: employeeId,
          attendance: []
        }
      }

      const attendanceToUpdate = [...newBulkChanges[employeeId].attendance]

      employee.attendance.forEach(att => {
        const attDate = dayjs(dataNormal?.attendance?.workingList?.find((w: any) => w.date === att.date)?.day_of_month)

        if (
          attDate.isValid() &&
          (attDate.isSame(start, 'day') ||
            attDate.isSame(end, 'day') ||
            (attDate.isAfter(start, 'day') && attDate.isBefore(end, 'day')))
        ) {
          const existingIndex = attendanceToUpdate.findIndex(a => a.date === att.date)

          if (existingIndex !== -1) {
            attendanceToUpdate[existingIndex] = {
              ...attendanceToUpdate[existingIndex],
              status: statusKey
            }
          } else {
            attendanceToUpdate.push({
              date: att.date,
              status: statusKey,
              day_of_month: dataNormal?.attendance?.workingList?.find((w: any) => w.date === att.date)?.day_of_month
            })
          }
        }
      })

      newBulkChanges[employeeId] = {
        attendance_id: employeeId,
        attendance: attendanceToUpdate
      }
    })

    if (setBulkChanges) {
      setBulkChanges(newBulkChanges)
    }
  }

  const handleMenuItemClick = (statusKey: string) => {
    methods.setValue('status', statusKey)
    setAnchorEl(null)

    const startDate = searchQuery.get('start_date')
    const endDate = searchQuery.get('end_date')

    if (startDate && endDate) {
      applyStatusToRange(statusKey, startDate, endDate)
    }
  }

  // Handle individual checkbox
  const handleIndividualCheck = (employee: IAttendanceEmployee, checked: boolean) => {
    const employeeId = Number(employee.id)
    const newSelectedUsers = new Set(selectedUsers)
    const newBulkChanges = { ...bulkChanges }

    if (checked) {
      newSelectedUsers.add(employeeId)

      newBulkChanges[employeeId] = {
        attendance_id: employeeId,
        attendance: []
      }
    } else {
      newSelectedUsers.delete(employeeId)

      delete newBulkChanges[employeeId]
    }

    const idsArray = Array.from(newSelectedUsers)

    onCheckboxChange?.(false, new Set(), newSelectedUsers, 0, [], idsArray)

    if (setBulkChanges) {
      setBulkChanges(newBulkChanges)
    }
  }

  const enum RowBackgroundForStatusEnum {
    MissingSystem = 'missing_system',
    MissingFile = 'missing_file',
    Unreconciled = 'unreconciled',
    Reconciled = 'reconciled'
  }

  const RowBackgroundForStatus = {
    [RowBackgroundForStatusEnum.MissingSystem]: '#ff4c5114',
    [RowBackgroundForStatusEnum.MissingFile]: '#ff9f4314',
    [RowBackgroundForStatusEnum.Unreconciled]: '#fff',
    [RowBackgroundForStatusEnum.Reconciled]: '#fff'
  }

  const getNextStatus = (currentStatus: string) => {
    switch (currentStatus) {
      case AttendanceEmployeeStatusEnum.WorkingDay:
        return AttendanceEmployeeStatusEnum.Absent
      case AttendanceEmployeeStatusEnum.Absent:
        return AttendanceEmployeeStatusEnum.NonWorkingDay
      case AttendanceEmployeeStatusEnum.NonWorkingDay:
        return AttendanceEmployeeStatusEnum.WorkingDay
      default:
        return AttendanceEmployeeStatusEnum.WorkingDay
    }
  }

  const handleUpdateAttendanceStatus = (
    attendanceId: number,
    employee: IAttendanceEmployee,
    day: number,
    dayOfMonth?: string
  ) => {
    const existingBulkData = bulkChanges[attendanceId]

    if (!existingBulkData) {
      n.error(__('Please select this employee first'))

      return
    }

    const mergedAttendance = getEmployeeAttendance(employee)
    const currentStatus = getAttendanceStatus(mergedAttendance as any, day)
    const nextStatus = getNextStatus(currentStatus)

    const attendanceToUpdate = [...existingBulkData.attendance]

    const existingIndex = attendanceToUpdate.findIndex(a => a.date === day)

    if (existingIndex !== -1) {
      attendanceToUpdate[existingIndex] = {
        ...attendanceToUpdate[existingIndex],
        status: nextStatus,
        date: day,
        day_of_month: dayOfMonth
      }
    } else {
      attendanceToUpdate.push({
        date: day,
        status: nextStatus,
        day_of_month: dayOfMonth
      })
    }

    if (setBulkChanges) {
      setBulkChanges({
        ...bulkChanges,
        [attendanceId]: {
          attendance_id: attendanceId,
          attendance: attendanceToUpdate
        }
      })
    }
  }

  const { mutate: attendanceDeleteEmployeeMutation } = useAttendanceDeleteEmployeeMutation()

  const handleDeleteEmployee = (id: string) => {
    n.confirm(__('Are you sure you want to delete this employee?'), {
      onConfirm: () => {
        attendanceDeleteEmployeeMutation(
          { id: Number(id) },
          {
            onSuccess: () => {
              n.success(__('Employee deleted successfully'))
              onSuccess?.()
            },
            onError: (error: IHttpError) => {
              n.error(error.message)
            }
          }
        )
      }
    })
  }

  return (
    <>
      <style>
        {`
          .current-day-cell::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: #3551f2;
            opacity: 0.1;
            pointer-events: none;
            z-index: 1;
          }

          .current-day-cell > * {
            position: relative;
            z-index: 2;
          }
        `}
      </style>
      <Divider />
      {getSelectedCount() > 0 && (
        <div className='flex items-center py-4 gap-4'>
          <div>
            <Button
              variant='outlined'
              onClick={e => {
                setSelectionAnchorEl(e.currentTarget)
              }}
              endIcon={<i className='tabler-chevron-down text-gray-600' />}
              sx={{
                borderRadius: '20px',
                borderColor: '#d0d0d0',
                color: '#666',
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#b0b0b0'
                }
              }}
            >
              <span className='text-sm font-medium'>
                {getSelectedCount()} {__('Selected')}
              </span>
            </Button>
            <Menu
              anchorEl={selectionAnchorEl}
              open={selectionOpen}
              onClose={() => {
                setSelectionAnchorEl(null)
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
              sx={{
                borderRadius: 2,
                mt: 1,
                minWidth: 200
              }}
            >
              <MenuItem
                onClick={() => {
                  onCheckboxChange?.(false, new Set(), new Set(), 0, [], [])
                  setSelectionAnchorEl(null)
                }}
                sx={{ fontSize: '14px' }}
              >
                {__('Un-Selected')}
              </MenuItem>
            </Menu>
          </div>
          <div>
            <InputDateRangerField
              value={
                searchQuery.get('start_date')
                  ? [searchQuery.get('start_date') as string, searchQuery.get('end_date') as string]
                  : []
              }
              onChange={created_at => {
                if (created_at) {
                  updateQuery({ start_date: created_at[0], end_date: created_at[1] })

                  const currentStatus = methods.getValues('status')

                  if (currentStatus && selectedUsers.size > 0) {
                    setTimeout(() => {
                      applyStatusToRange(currentStatus, created_at[0], created_at[1], false)
                    }, 0)
                  }
                } else {
                  updateQuery({ start_date: undefined, end_date: undefined })
                }
              }}
              minDate={minDate}
              maxDate={maxDate}
              format='YYYY-MM-DD'
            />
          </div>
          <div>
            <Button
              variant='contained'
              color='primary'
              onClick={e => {
                setAnchorEl(e.currentTarget)
              }}
              endIcon={<i className='tabler-chevron-down text-white' />}
              className='shadow-none'
              sx={{
                '&.MuiButtonBase-root': {
                  backgroundColor:
                    methods.getValues('status') === AttendanceEmployeeStatusEnum.WorkingDay
                      ? '#577dfd'
                      : methods.getValues('status') === AttendanceEmployeeStatusEnum.Absent
                        ? '#ff7074'
                        : '#c0bec5'
                }
              }}
            >
              {methods.getValues('status')
                ? attendanceEmployeeStatusLabel[
                    methods.getValues('status') as keyof typeof attendanceEmployeeStatusLabel
                  ]
                : __('Choose your option')}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={() => {
                setAnchorEl(null)
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
              sx={{
                borderRadius: 2,
                mt: 1,
                minWidth: 200
              }}
            >
              {Object.entries(attendanceEmployeeStatusLabel).map(([key, value]) => (
                <MenuItem key={key} className='rounded-full' onClick={() => handleMenuItemClick(key)}>
                  {value}
                </MenuItem>
              ))}
            </Menu>
          </div>
        </div>
      )}
      <Box sx={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            border: '1px solid #e0e0e0'
          }}
        >
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderRight: '1px solid #e0e0e0',
                  borderBottom: '1px solid #e0e0e0',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
              >
                <div className='flex items-center gap-2'>
                  <Checkbox
                    checked={dataWithId.length > 0 && dataWithId.every(emp => selectedUsers.has(Number(emp.id)))}
                    indeterminate={
                      dataWithId.some(emp => selectedUsers.has(Number(emp.id))) &&
                      !dataWithId.every(emp => selectedUsers.has(Number(emp.id)))
                    }
                    onChange={e => handleCheckAll(e.target.checked)}
                  />
                  <span className='text-sm text-gray-600'>({getSelectedCount()})</span>
                </div>
              </th>
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderRight: '1px solid #e0e0e0',
                  borderBottom: '1px solid #e0e0e0',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
              >
                {__('Employee')}
              </th>
              {headerWorkingList.map((item, index) => {
                const isCurrentDay = item.day_of_month
                  ? dayjs().isSame(dayjs(item.day_of_month), 'day')
                  : currentDay !== null && item.date === currentDay

                const minimumWorkingDays = dataNormal?.minimum_working_days
                const isMinimumWorkingDayColumn = minimumWorkingDays && index < minimumWorkingDays
                const withdrawCutoffDay = Number(dataNormal?.company?.withdraw_cutoff_day || 0)
                const currentDayOfMonth = item.day_of_month ? dayjs(item.day_of_month).date() : Number(item.date)
                const isWithdrawCutoffBlocked = withdrawCutoffDay > 0 && currentDayOfMonth > withdrawCutoffDay
                const backgroundColor = isMinimumWorkingDayColumn || isWithdrawCutoffBlocked ? '#d5d4d8' : undefined

                return (
                  <th
                    key={`header-${item.day_of_month || item.date}`}
                    className={isCurrentDay ? 'current-day-cell' : ''}
                    style={{
                      padding: '12px 8px',
                      textAlign: 'center',
                      borderBottom: '1px solid #e0e0e0',
                      borderRight: '1px solid #e0e0e0',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      minWidth: '40px',
                      backgroundColor,
                      position: 'relative'
                    }}
                  >
                    {item.day_of_month ? dayjs(item.day_of_month).date() : item.date}
                  </th>
                )
              })}
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  borderBottom: '1px solid #e0e0e0',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  minWidth: '150px'
                }}
              >
                {__('Worked Day This Month')}
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Attendance Assumption Row */}
            <tr
              style={{
                borderBottom: '1px solid #e0e0e0',
                backgroundColor: '#3551f214'
              }}
            >
              <td
                style={{
                  padding: '12px 16px 12px 21px',
                  textAlign: 'left',
                  borderRight: '1px solid #e0e0e0',
                  borderBottom: '1px solid #e0e0e0',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
              >
                <i className='tabler-star-filled' style={{ color: '#3551f2', fontSize: '24px' }} />
              </td>
              <td
                style={{
                  padding: '12px 16px',
                  borderRight: '1px solid #e0e0e0',
                  verticalAlign: 'middle',
                  fontWeight: '400',
                  color: '#3551f2'
                }}
              >
                {__('Attendance Assumption')}
              </td>
              {headerWorkingList.map((item, dayIndex) => {
                const dayData = attendanceAssumption?.attendance?.workingList?.find(
                  (workingItem: any) => workingItem.date === item.date
                )

                const isWorkingDay = dayData?.status === 'working_day'

                let color = '#c0bec5' // default non-working day

                if (isWorkingDay) {
                  color = '#577dfd' // working day
                }

                const isCurrentDay = item.day_of_month
                  ? dayjs().isSame(dayjs(item.day_of_month), 'day')
                  : currentDay !== null && item.date === currentDay

                const minimumWorkingDays = dataNormal?.minimum_working_days
                const isMinimumWorkingDayColumn = minimumWorkingDays && dayIndex < minimumWorkingDays
                const withdrawCutoffDay = Number(dataNormal?.company?.withdraw_cutoff_day || 0)
                const currentDayOfMonth = item.day_of_month ? dayjs(item.day_of_month).date() : Number(item.date)
                const isWithdrawCutoffBlocked = withdrawCutoffDay > 0 && currentDayOfMonth > withdrawCutoffDay
                const backgroundColor = isMinimumWorkingDayColumn || isWithdrawCutoffBlocked ? '#2e263d14' : undefined

                return (
                  <td
                    key={`assumption-day-${item.day_of_month || item.date}`}
                    className={isCurrentDay ? 'current-day-cell' : ''}
                    style={{
                      padding: '8px',
                      textAlign: 'center',
                      borderRight: '1px solid #e0e0e0',
                      verticalAlign: 'middle',
                      backgroundColor,
                      position: 'relative'
                    }}
                  >
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: color,
                        margin: '0 auto',
                        display: 'inline-block'
                      }}
                    />
                  </td>
                )
              })}
              <td
                style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  verticalAlign: 'middle',
                  fontWeight: 'bold'
                }}
              >
                {attendanceAssumption?.attendance?.workingDay || '-'}
              </td>
            </tr>

            {dataWithId.map((employee, index) => (
              <tr
                key={`employee-${employee.id}-${index}`}
                style={{
                  borderBottom: '1px solid #e0e0e0',
                  backgroundColor: RowBackgroundForStatus[employee.status as keyof typeof RowBackgroundForStatus]
                }}
              >
                <td
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderRight: '1px solid #e0e0e0',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  <Checkbox
                    checked={selectedUsers.has(Number(employee.id))}
                    onChange={e => handleIndividualCheck(employee, e.target.checked)}
                  />
                </td>
                <td
                  style={{
                    padding: '12px 16px',
                    borderRight: '1px solid #e0e0e0',
                    verticalAlign: 'middle'
                  }}
                >
                  <div className='flex items-center gap-2 justify-between'>
                    <div className='flex items-center gap-2'>
                      <Tooltip
                        title={
                          employee.status === RowBackgroundForStatusEnum.MissingFile
                            ? __('This employee exists in the system but is not in the file')
                            : employee.status === RowBackgroundForStatusEnum.MissingSystem
                              ? __('This employee is in the file but not in the system')
                              : ''
                        }
                        arrow
                        slots={{
                          transition: Zoom
                        }}
                        placement='right'
                      >
                        <div>
                          <UserAvatar
                            user={employee as any}
                            link={
                              employee.status === RowBackgroundForStatusEnum.MissingSystem && employee.user_id === null
                                ? 'javascript:void(0)'
                                : `/cms/attendance/employee/${employee.user_id}?month=${searchQuery.get('month')}&companyId=${searchQuery.get('companyId') || id}`
                            }
                          />
                        </div>
                      </Tooltip>
                      {Number(employee?.daily_wage) <= 0 && (
                        <i className='tabler-info-circle text-[#ff9f43]' style={{ fontSize: 18 }} />
                      )}
                    </div>
                    {employee.is_allow_delete && (
                      <div>
                        <IconButton onClick={() => handleDeleteEmployee(employee?.id)}>
                          <i className='tabler-trash text-[#2F2B3DB2] size-[18px]' />
                        </IconButton>
                      </div>
                    )}
                  </div>
                </td>
                {headerWorkingList.map((item, dayIndex) => {
                  const day = item.date
                  const employeeAttendance = getEmployeeAttendance(employee)
                  const found = employeeAttendance.find((a: any) => a.date === day)
                  const status = found?.status
                  const color = status ? getAttendanceDotColor(status) : ''

                  const isCurrentDay = item.day_of_month
                    ? dayjs().isSame(dayjs(item.day_of_month), 'day')
                    : currentDay !== null && day === currentDay

                  // Check if this column should have minimum working days background
                  const minimumWorkingDays = dataNormal?.minimum_working_days
                  const isMinimumWorkingDayColumn = minimumWorkingDays && dayIndex < minimumWorkingDays
                  const withdrawCutoffDay = Number(dataNormal?.company?.withdraw_cutoff_day || 0)
                  const currentDayOfMonth = item.day_of_month ? dayjs(item.day_of_month).date() : Number(item.date)
                  const isWithdrawCutoffBlocked = withdrawCutoffDay > 0 && currentDayOfMonth > withdrawCutoffDay
                  const backgroundColor = isMinimumWorkingDayColumn || isWithdrawCutoffBlocked ? '#2e263d14' : undefined

                  // Check if employee is selected
                  const isEmployeeSelected = selectedUsers.has(Number(employee.id))

                  return (
                    <td
                      key={`employee-${employee.id}-${index}-day-${item.day_of_month || day}`}
                      className={isCurrentDay ? 'current-day-cell' : ''}
                      style={{
                        padding: '8px',
                        textAlign: 'center',
                        borderRight: '1px solid #e0e0e0',
                        verticalAlign: 'middle',
                        backgroundColor,
                        position: 'relative'
                      }}
                    >
                      {status ? (
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: color,
                            margin: '0 auto',
                            display: 'inline-block',
                            cursor: isEmployeeSelected ? 'pointer' : 'not-allowed',
                            transition: 'transform 0.2s, box-shadow 0.2s, opacity 0.2s'
                          }}
                          onClick={() => {
                            if (isEmployeeSelected) {
                              handleUpdateAttendanceStatus(Number(employee.id), employee, day, item.day_of_month)
                            }
                          }}
                          onMouseEnter={e => {
                            if (isEmployeeSelected) {
                              e.currentTarget.style.transform = 'scale(1.2)'
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)'
                            }
                          }}
                          onMouseLeave={e => {
                            if (isEmployeeSelected) {
                              e.currentTarget.style.transform = 'scale(1)'
                              e.currentTarget.style.boxShadow = 'none'
                            }
                          }}
                        />
                      ) : null}
                    </td>
                  )
                })}
                <td
                  style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    verticalAlign: 'middle',
                    fontWeight: 'bold'
                  }}
                >
                  {countWorkingDays(getEmployeeAttendance(employee) as any)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
      {meta && (
        <TablePagination
          meta={
            {
              ...meta,
              page: Number(searchQuery.get('page')) || 1
            } as unknown as TablePaginationProps
          }
        />
      )}
      <ConfirmDialog />
    </>
  )
}
